import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'grid' | 'list';
  rank?: number;
}

export default function ArticleCard({ article, variant = 'grid', rank }: ArticleCardProps) {
  if (variant === 'list') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-center gap-4 py-4 border-b border-background-200/60 cursor-pointer"
      >
        {rank && (
          <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-lg font-bold text-foreground-300 font-heading">
            {String(rank).padStart(2, '0')}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
            {article.title}
          </h3>
          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            <span className="text-xs text-primary-500 font-medium">{article.category}</span>
            <span className="text-xs text-foreground-400">{article.timeAgo}</span>
            <span className="text-xs text-foreground-400">热度 {article.heat}</span>
          </div>
        </div>
        <div className="flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden bg-background-200">
          <img
            src={article.imageThumb}
            alt={article.title}
            title={`${article.title} - 911爆料网`}
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block cursor-pointer"
    >
      <div className="relative aspect-[4/3] rounded-lg overflow-hidden bg-background-200 mb-3">
        <img
          src={article.image}
          alt={article.title}
          title={`${article.title} - 911爆料网`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        {article.isTop && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-bold bg-primary-500/90 text-background-50 backdrop-blur-sm">
            爆
          </span>
        )}
        {article.isVerified && (
          <span className="absolute top-3 right-3 px-2 py-1 rounded text-xs font-medium bg-background-50/80 text-foreground-800 backdrop-blur-sm">
            <i className="ri-verified-badge-line w-3.5 h-3.5 inline-flex items-center justify-center mr-0.5"></i>
            已核实
          </span>
        )}
      </div>
      <div className="px-0.5">
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <span className="text-xs font-medium text-primary-500">{article.category}</span>
          <span className="text-xs text-foreground-400">{article.timeAgo}</span>
        </div>
        <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors duration-200">
          {article.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1.5 line-clamp-2 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-3 mt-2.5">
          <span className="text-xs text-accent-500 font-semibold">
            <i className="ri-fire-line w-3.5 h-3.5 inline-flex items-center justify-center mr-0.5"></i>
            {article.heat}
          </span>
          <span className="text-xs text-foreground-400">{article.views} 阅读</span>
        </div>
      </div>
    </Link>
  );
}